import { TitleCasePipe } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    OnInit,
    signal,
    ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
    TelXLDatepickerComponent,
    TelXLDropdownComponent,
    TelXLLoadingComponent,
    TelXLMultiselectDropdownComponent,
} from '@dxp/elements';
import {
    IActivityHistoryFilterStateTypeId,
    ICustomerRelations,
    IDateRangeSelection,
    IDropdown,
    IHistoricActivities,
} from '@dxp/core/models';
import { fromEvent } from 'rxjs';
import { debounceTime, filter, map, switchMap, tap } from 'rxjs/operators';
import {
    addMonths,
    endOfDay,
    endOfToday,
    startOfDay,
    startOfToday,
} from 'date-fns';
import { SettingsService } from '@dxp/core/services';
import { ChannelType, Direction } from '@dxp/core/types';
import { USER_STORE } from '@dxp/core/stores';
import { ActivityHistoryService } from './../services/activity-history.service';
import { TelXLActivityHistoryItemComponent } from './../item/activity-history-item.component';

const LIMIT = 20;

@Component({
    selector: 'telxl-shared-customer-activity-history-panel',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TelXLLoadingComponent,
        TelXLActivityHistoryItemComponent,
        TelXLDropdownComponent,
        TelXLMultiselectDropdownComponent,
        TelXLDatepickerComponent,
    ],
    providers: [TitleCasePipe],
    templateUrl: './customer-activity-history-panel.component.html',
    styleUrl: './customer-activity-history-panel.component.scss',
})
export class TelXLCustomerActivityHistoryPanelComponent
    implements OnInit, AfterViewInit
{
    readonly #destroyRef = inject(DestroyRef);
    readonly #settingsService = inject(SettingsService);
    readonly #activityHistoryService = inject(ActivityHistoryService);
    readonly #titleCasePipe = inject(TitleCasePipe);
    readonly #userStore = inject(USER_STORE);

    activityHistoryState = this.#settingsService.customerActivityHistoryState;

    customer = input.required<string>();
    height = input.required<string>();

    @ViewChild('scrollContainer', { static: false })
    scrollContainer!: ElementRef;

    readonly customerActivity = signal<IHistoricActivities>({
        count: 0,
        historicActivity: [],
    });

    readonly relations = signal<ICustomerRelations>({
        customerId: '',
        agents: [],
        channels: [],
        completionCodes: [],
    });

    readonly customerUpdated = computed(() => this.customer());

    readonly isLoading = signal<boolean>(false);
    readonly fromDate = computed<Date>(
        () => this.activityHistoryState().dateFrom,
    );

    readonly toDate = computed<Date | null>(
        () => this.activityHistoryState().dateTo,
    );
    readonly today = signal<Date>(new Date());

    readonly sort = computed<'oldest' | 'newest'>(
        () => this.activityHistoryState().sortSelection,
    );
    readonly agents = computed<string[]>(
        () => this.activityHistoryState().queueOrAgentSelection,
    );
    readonly channels = computed<number[]>(
        () => this.activityHistoryState().channelSelection,
    );
    readonly channelTypes = computed<ChannelType[]>(
        () => this.activityHistoryState().channelTypesSelection,
    );
    readonly direction = computed<Direction>(
        () => this.activityHistoryState().directionSelection,
    );
    readonly completionCodes = computed<string[]>(
        () => this.activityHistoryState().completionCodeSelection,
    );

    agentMenuItems = computed<IDropdown<string>[]>(() =>
        this.relations().agents.map(agent => ({
            label: agent.name,
            value: agent.id,
            disabled: false,
        })),
    );

    channelMenuItems = computed<IDropdown<number>[]>(() =>
        this.relations().channels.map(channel => ({
            label: channel.name,
            value: channel.id,
            disabled: false,
        })),
    );

    readonly mediaMenuItems = computed<IDropdown<ChannelType>[]>(() => {
        const titleCase = this.#titleCasePipe.transform.bind(
            this.#titleCasePipe,
        );
        const uniqueChannels = Array.from(
            new Map(
                this.relations().channels.map(channel => [
                    channel.channelType,
                    channel,
                ]),
            ).values(),
        );

        return uniqueChannels.map(channel => ({
            label: titleCase(channel.channelType),
            value: channel.channelType,
            disabled: false,
        }));
    });

    readonly directionMenuItems = computed<IDropdown<Direction>[]>(() => [
        {
            label: 'Both',
            value: 'All',
            disabled: false,
        },
        {
            label: 'Inbound',
            value: 'InBound',
            disabled: false,
        },
        {
            label: 'Outbound',
            value: 'OutBound',
            disabled: false,
        },
    ]);

    readonly sortMenuItems = computed<IDropdown<'oldest' | 'newest'>[]>(() => [
        {
            label: 'Ascending',
            value: 'oldest',
            disabled: false,
        },
        {
            label: 'Descending',
            value: 'newest',
            disabled: false,
        },
    ]);

    readonly hideAgentsMenu = computed<boolean>(() =>
        this.#userStore.hideSensitiveAgentInformation(),
    );

    completionCodeMenuItems = computed<IDropdown<string>[]>(() =>
        this.relations()
            .completionCodes.filter(completionCode => completionCode.length > 0)
            .map(completionCode => ({
                label: completionCode,
                value: completionCode,
                disabled: false,
            })),
    );

    constructor() {
        toObservable(this.customer)
            .pipe(
                switchMap(customer =>
                    this.#activityHistoryService
                        .fetchCustomerRelations(customer)
                        .pipe(
                            tap(relations => {
                                this.relations.set(relations);
                                this.#checkState();
                            }),
                        ),
                ),
                takeUntilDestroyed(this.#destroyRef),
            )
            .subscribe();
    }

    #checkState() {
        if (this.activityHistoryState().subjectId !== this.customer()) {
            this.clearAllFilters();
        } else {
            this.#reload();
        }
    }

    ngOnInit() {
        this.#checkState();
    }

    ngAfterViewInit() {
        if (this.scrollContainer) {
            fromEvent(this.scrollContainer.nativeElement, 'scroll')
                .pipe(
                    debounceTime(300),
                    map(() => this.scrollContainer.nativeElement),
                    filter(
                        container =>
                            container.scrollTop + container.clientHeight >=
                            container.scrollHeight - 50,
                    ),
                    tap(() => this.#loadActivity(false)),
                    takeUntilDestroyed(this.#destroyRef),
                )
                .subscribe();
        }
        this.#reload();
    }

    onSortChanged(sort: 'oldest' | 'newest') {
        this.activityHistoryState.set({
            ...this.activityHistoryState(),
            sortSelection: sort,
        });
        this.#reload();
    }

    onAgentsChanged(agents: string[]) {
        this.activityHistoryState.set({
            ...this.activityHistoryState(),
            queueOrAgentSelection: agents,
        });
        this.#reload();
    }

    onChannelsChanged(channels: number[]) {
        this.activityHistoryState.set({
            ...this.activityHistoryState(),
            channelSelection: channels,
        });
        this.#reload();
    }

    onChannelTypesChanged(channelTypes: ChannelType[]) {
        this.activityHistoryState.set({
            ...this.activityHistoryState(),
            channelTypesSelection: channelTypes,
        });
        this.#reload();
    }

    onDirectionChanged(direction: Direction) {
        this.activityHistoryState.set({
            ...this.activityHistoryState(),
            directionSelection: direction,
        });
        this.#reload();
    }

    onCompletionCodesChanged(completionCodes: string[]) {
        this.activityHistoryState.set({
            ...this.activityHistoryState(),
            completionCodeSelection: completionCodes,
        });
        this.#reload();
    }

    onDateRangeSelected(range: IDateRangeSelection) {
        this.activityHistoryState.set({
            ...this.activityHistoryState(),
            dateFrom: startOfDay(range.from ?? addMonths(startOfToday(), -1)),
        });
        this.activityHistoryState.set({
            ...this.activityHistoryState(),
            dateTo: range.to ? endOfDay(range.to) : null,
        });

        if (
            (this.fromDate() === null && this.toDate() === null) ||
            (this.fromDate() !== null && this.toDate() !== null)
        ) {
            this.#reload();
        }
    }

    clearAllFilters() {
        this.activityHistoryState.set({
            queueOrAgentSelection: [],
            channelSelection: [],
            channelTypesSelection: [],
            completionCodeSelection: [],
            directionSelection: 'All',
            sortSelection: 'newest',
            typeId: IActivityHistoryFilterStateTypeId.CUSTOMER_ACTIVITY_HISTORY,
            subjectId: this.customer(),
            dateFrom: addMonths(startOfToday(), -1),
            dateTo: endOfToday(),
        });
        this.#reload();
    }

    #hasMoreData(): boolean {
        return (
            this.customerActivity().historicActivity.length <
            this.customerActivity().count
        );
    }

    #loadActivity(initialLoad: boolean) {
        if ((!initialLoad && !this.#hasMoreData()) || this.isLoading()) return;

        this.isLoading.set(true);

        if (!this.toDate())
            this.activityHistoryState.set({
                ...this.activityHistoryState(),
                dateTo: endOfToday(),
            });

        this.#activityHistoryService
            .fetchCustomerActivity(
                this.customer(),
                LIMIT,
                this.customerActivity().historicActivity.length,
                {
                    agents: this.agents(),
                    channels: this.channels(),
                    channelTypes: this.channelTypes(),
                    direction: this.direction(),
                    completionCodes: this.completionCodes(),
                    date: { from: this.fromDate(), to: this.toDate() },
                },
                this.sort(),
            )
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe(data => {
                this.customerActivity.update(current => {
                    return {
                        count: data.count,
                        historicActivity: [
                            ...current.historicActivity,
                            ...data.historicActivity,
                        ],
                    };
                });

                this.isLoading.set(false);
            });
    }

    #reload() {
        this.customerActivity.set({
            count: 0,
            historicActivity: [],
        });

        this.#loadActivity(true);
    }
}
